import React, { useState, useRef, useEffect, useContext } from 'react';
import { Link } from 'gatsby';
import { motion, useCycle } from 'framer-motion';

import { AnalyticsContext } from '../../analytics/AnalyticsContext';

// Hooks
import useBrowserStorage from '../../hooks/useBrowserStorage/useBrowserStorage';

// Components
import MenuToggle from './MenuToggle';
import MobileNavbarMenu from './MobileNavbarMenu';

// Icons
import { SearchIcon, SpectrumLogo } from '../Icons';

import './MobileNavbar.scss';

// TEALIUM
import initTealiumTracker from 'tealium-tracker';

let tealiumTrackEvent;
if (typeof window !== 'undefined') {
  const { trackEvent } = initTealiumTracker({ debugMode: true });
  tealiumTrackEvent = trackEvent;
}

const MobileNavbar = () => {
  const { analytics } = useContext(AnalyticsContext);
  const [isMenuOpen, setIsMenuOpen] = useCycle<boolean>(false, true);
  const [currentPath, setCurrentPath] = useState<string>('');
  const navbarRef = useRef<HTMLElement | null>(null);
  const searchTrigger = useRef<HTMLAnchorElement | null>(null);

  const [shouldTriggerFocus, setShouldFocusTrigger] = useBrowserStorage(
    'sessionStorage',
    'shouldFocusTrigger',
    false
  );

  // helper function for quantum analytics tagging
  const quantumTrack = (currPageElemStdName) => {
    analytics.track('SpectrumOnDemand_selectAction_navigationClick', {
      currPageElemStdName,
      currPageElemUiName: currPageElemStdName,
    });
  };

  // Passes current url to preserve browser history on refresh
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = window.location.pathname || '';
      const params = window.location.search || '';
      setCurrentPath(`${url}${params}`);
    }
  }, []);

  useEffect(() => {
    // if coming from the search page, put focus back onto the search icon
    if (shouldTriggerFocus) {
      searchTrigger?.current?.focus();
    }

    return () => {
      // remove the search trigger focus flag (it will be set again on search page when needed)
      if (shouldTriggerFocus) {
        setShouldFocusTrigger(false);
      }
    };
  }, [searchTrigger, shouldTriggerFocus, setShouldFocusTrigger]);

  // handles focus trap
  useEffect(() => {
    if (navbarRef && navbarRef.current) {
      const focusable: HTMLElement[] = Array.from(
        navbarRef?.current.querySelectorAll(
          'button:not([tabindex="-1"]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      );

      if (focusable?.length) {
        const firstElement: HTMLElement = focusable[0];
        const lastElement: HTMLElement = focusable[focusable.length - 1];

        const trapFocus = (e: KeyboardEvent) => {
          if (e.key === 'Tab') {
            if (e.shiftKey) {
              if (document.activeElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
              }
            } else {
              if (document.activeElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
              }
            }
          }
        };

        navbarRef.current.addEventListener('keydown', trapFocus);

        const storedRef = navbarRef.current;
        return () => {
          storedRef?.removeEventListener('keydown', trapFocus);
        };
      }
    }
  }, [navbarRef]);

  return (
    <motion.nav
      className="mobile-nav"
      initial={false}
      animate={isMenuOpen ? 'open' : 'closed'}
      ref={navbarRef}
    >
      <div className="mobile-nav__left">
        <MenuToggle toggle={() => setIsMenuOpen()} />
        {/* Logo */}
        <Link
          to="/"
          aria-label="Spectrum logo"
          className="mobile-nav__logo"
          tabIndex={isMenuOpen ? -1 : 0}
          onClick={() => quantumTrack('home')}
        >
          <SpectrumLogo />
        </Link>
      </div>

      {/* Search Trigger */}
      <Link
        className="mobile-nav__search"
        innerRef={searchTrigger}
        title="Search"
        aria-label="Search"
        to="/search/"
        state={{ previousPath: currentPath }}
        data-linktype="header"
        data-linkname="SearchIcon"
        onClick={() => {
          quantumTrack('search');
          tealiumTrackEvent({
            linktype: 'header',
            linkname: 'SearchIcon',
          });
        }}
      >
        <SearchIcon />
      </Link>

      <MobileNavbarMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen()}
        analytics={analytics}
      />
    </motion.nav>
  );
};

export default MobileNavbar;
